import { Typography } from 'maple-storybook'
import { fileAttachmentThumbnails } from 'maple-storybook/utils'
import moment from 'moment'

import { darkGrayColor, whiteChipColor } from 'variables/styles'

import { checkArrayPresence, formatLongText } from './applicationHelper'
import { PMS_DATE_FORMAT, TWO_DIGIT_FORMAT } from './constants'
import { formatDate, getYearFromDate } from './datesHelper'
import { checkPermission } from './permissionsHelper'

export const ConfigTabs = [
  {
    value: '/performance-management/team',
    label: 'Team',
    href: '/performance-management/team'
  },
  {
    value: '/performance-management/review-settings',
    label: 'Review Settings',
    href: '/performance-management/review-settings'
  }
]

export const reviewSearchFilters = [
  {
    label: 'Department',
    value: 'departments',
    component: 'selectWrapper',
    placeholder: 'Select Department(s)',
    multiOption: true,
    allCheck: true
  },
  {
    label: 'Designations',
    value: 'designations',
    component: 'selectWrapper',
    placeholder: 'Select Designation(s)',
    multiOption: true,
    allCheck: true
  },
  {
    label: 'Employment Types',
    value: 'employment_types',
    component: 'selectWrapper',
    placeholder: 'Select Employment Type(s)',
    multiOption: true,
    allCheck: true
  },
  {
    label: 'Location',
    value: 'locations',
    component: 'selectWrapper',
    placeholder: 'Select Location(s)',
    multiOption: true,
    allCheck: true
  },
  {
    label: 'Review Period',
    value: 'review_period',
    component: 'selectWrapper',
    placeholder: 'Select Review Period(s)'
  },
  {
    label: 'Status',
    value: 'review_setting_status',
    component: 'selectWrapper',
    placeholder: 'Select Status(s)'
  }
]

export const isConfigTabs = (path) =>
  ['/performance-management/team', '/performance-management/review-settings'].includes(path) || false

export const myTeamTabs = [
  {
    value: '/performance/my-team',
    label: 'Teammates',
    href: '/performance/my-team'
  },
  {
    value: '/performance/kpi-and-goals',
    label: "Goals & KPI's",
    href: '/performance/kpi-and-goals'
  }
]

export const isMyTeamTabs = (path) => ['/performance/my-team', '/performance/kpi-and-goals'].includes(path) || false

export const applicableOnOptions = [
  { label: 'Department', value: 'departments' },
  { label: 'Designation', value: 'designations' },
  { label: 'Employment Type', value: 'employment_types' },
  { label: 'Location', value: 'locations' }
]

export const getApplicableOnData = (EmploymentType, Department, Title, Location) => {
  const array = []
  if (checkArrayPresence(EmploymentType)) array.push({ name: 'Employment Type' })
  if (checkArrayPresence(Department)) array.push({ name: 'Department' })
  if (checkArrayPresence(Title)) array.push({ name: 'Designation' })
  if (checkArrayPresence(Location)) array.push({ name: 'Location' })
  return array
}

export const getBindableTitles = (data) => {
  const result = {
    Department: [],
    Title: [],
    Location: []
  }
  data.forEach((item) => {
    if (result[item.bindable_type]) {
      result[item.bindable_type].push({ id: item.id, value: item.bindable_id, name: item.bindable_title })
    }
  })

  return result
}

export const getConfigInitialPayload = (payload) => {
  const initialValues = { ...payload }
  delete initialValues.performance_review_settings_bindings_data
  delete initialValues.impact_area_settings_attributes_data
  delete initialValues.reminder_settings_attributes_data

  const result = getBindableTitles(payload.performance_review_settings_bindings_data)

  initialValues.departments = result.Department
  initialValues.designations = result.Title
  initialValues.locations = result.Location
  initialValues.impact_area_settings_attributes = payload.impact_area_settings_attributes_data
  initialValues.reminder_settings_attributes = payload.reminder_settings_attributes_data
  initialValues.review_setting_status = !payload.deleted_at

  return initialValues
}

export const reviewTriggerStatusOptions = [
  { label: 'Triggered', value: 'triggered' },
  { label: 'Not Triggered', value: 'not_triggered' },
  { label: 'Archived', value: 'archived' }
]

export const publishStatusOptions = [
  { label: 'Pending Publish', value: 'pending' },
  { label: 'Published', value: 'published' }
]

export const archivedFilterOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Archived', value: 'archived' }
]

export const triggeredOnOptions = [
  {
    label: 'Review Start Date',
    value: 'review_start_date'
  },
  {
    label: 'Review End Date',
    value: 'review_end_date'
  }
]

export const startEndOptions = (start, end, percentage = false) => {
  const numberArray = []
  for (let i = start; i <= end; i++) {
    numberArray.push({ label: `${i.toString()} ${percentage ? '%' : ''}`, value: i.toString() })
  }
  return numberArray
}

export const userKpiInputOptions = startEndOptions(0, 100, true)

export const reviewFrequencyOptions = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Bi-Annually', value: 'biannual' },
  { label: 'Annually', value: 'annual' }
]

export const mapNumberWithSequence = (number) => {
  switch (number % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

export const reviewStatusOptions = [
  {
    id: 0,
    label: 'Inactive',
    value: 'inactive'
  },
  {
    id: 1,
    label: 'Active',
    value: 'active'
  }
]

export const getTotalWeightPercentage = (values) => {
  let impactWeightPercentage = 100
  values?.impact_areas_attributes?.forEach((item) => {
    if (item?.weight) {
      impactWeightPercentage -= Number(item.weight)
    }
  })
  return impactWeightPercentage < 0 ? 0 : impactWeightPercentage
}

export const reviewConfigFilters = [
  { value: 'review_period', label: 'Review Period' },
  { value: 'review_setting_status', label: 'Status' }
]

export const userReviewStatuses = [
  { label: 'In Progress', value: 'in_progress', color: 'blue-700' },
  { label: 'Over Due', value: 'overdue', color: 'warning-500' },
  { label: 'Completed', value: 'completed', color: 'primary-600' },
  { label: 'Published', value: 'published', color: 'purple-600' }
]

export const reviewSubmissionStatusClassMap = {
  '(Submitted)': 'review-status-submitted',
  '(Draft)': 'review-status-draft'
}

export const myTeammatesFilters = (allFilters = false, configView, customSetter) => {
  let filters = [
    {
      label: 'Review Type',
      value: 'review_type',
      component: 'selectWrapper',
      multiOption: true,
      allCheck: true,
      placeholder: 'Select Review Type'
    },
    {
      label: 'Department',
      value: 'department_ids',
      component: 'selectWrapper',
      placeholder: 'Select Department',
      multiOption: true,
      allCheck: true
    },
    {
      label: 'Designation',
      value: 'designation_ids',
      component: 'selectWrapper',
      placeholder: 'Select Designation',
      multiOption: true,
      allCheck: true
    },
    {
      label: 'Status',
      value: 'status',
      component: 'selectWrapper',
      placeholder: 'Select Status'
    }
  ]
  if (allFilters) {
    filters.splice(0, 0, {
      label: 'Date Range',
      value: 'custom_date_range',
      fromDateValue: 'start_date',
      toDateValue: 'end_date',
      component: 'dateRangePickerWrapper'
    })

    filters.splice(
      5,
      0,
      {
        label: 'Reviewer Type',
        value: 'reviewer_type',
        component: 'selectWrapper',
        nestedSelection: true,
        selectedValue: customSetter.selectedReviewerType,
        setValue: customSetter.setSelectedReviewerType,
        placeholder: 'Select Review Type',
        nestedOptions: [
          {
            label: 'Tier',
            value: 'tier',
            component: 'selectWrapper',
            placeholder: 'Select Tier',
            conditionToRender: customSetter.selectedReviewerType
          }
        ]
      },
      {
        label: 'Lead',
        value: 'lead_id',
        component: 'selectWrapper',
        userSearchable: true,
        placeholder: 'Select Lead'
      },
      {
        label: 'Manager',
        value: 'manager_id',
        component: 'selectWrapper',
        userSearchable: true,
        placeholder: 'Select Manager'
      },
      {
        label: 'Reporting',
        value: 'reporting_to',
        userSearchable: true,
        component: 'selectWrapper',
        placeholder: 'Select Reporting'
      }
    )
  }
  if (configView) {
    filters.splice(2, 0, {
      label: 'Reviewer',
      placeholder: 'Select Reviewer',
      component: 'selectWrapper',
      value: 'reviewer_ids',
      userSearchable: true,
      multiOption: true
    })
  }
  return filters
}

export const myTeamReviewTableHeaders = (config, { excludeTeam = false } = {}) => {
  let array = ['Employee', 'Designation', 'Department', 'Review Type', 'Status', 'Team']
  if (config) array.splice(4, 0, 'Reviewer')
  if (excludeTeam) array = array.filter((header) => header !== 'Team')
  return array
}

const TENURE_FILTER_LABEL = 'Tenure'

export const BIANNUAL_APRIL_MONTH = 4
export const BIANNUAL_SEPTEMBER_MONTH = 9
export const BIANNUAL_OCTOBER_MONTH = 10
export const BIANNUAL_MARCH_MONTH = 3

const FISCAL_YEAR_START_MONTH = BIANNUAL_APRIL_MONTH

const biannualAprilPeriodStartValue = (year) => `1-${BIANNUAL_APRIL_MONTH}-${year}`
const biannualOctoberPeriodStartValue = (year) => `1-${BIANNUAL_OCTOBER_MONTH}-${year}`

const isInBiannualAprilPeriod = (month) => month >= BIANNUAL_APRIL_MONTH && month <= BIANNUAL_SEPTEMBER_MONTH

const fiscalYearStartYear = (date) => {
  const month = moment(date).month() + 1
  const year = getYearFromDate(date)
  return month >= FISCAL_YEAR_START_MONTH ? year : year - 1
}

export const defaultQuarterForReviewPeriod = (reviewPeriod) => getCurrentPeriodDate(reviewPeriod ?? '1')

export const getCurrentPeriodDate = (periodType, date = new Date()) => {
  const year = getYearFromDate(date)
  const month = moment(date).month() + 1
  if (periodType === '0') return `1-${month}-${year}`
  if (periodType === '2') {
    if (isInBiannualAprilPeriod(month)) return biannualAprilPeriodStartValue(year)
    if (month >= BIANNUAL_OCTOBER_MONTH) return biannualOctoberPeriodStartValue(year)
    return biannualOctoberPeriodStartValue(year - 1)
  }
  if (periodType === '3') return `1-4-${fiscalYearStartYear(date)}`
  return getCurrentQuarterDate(date)
}

export const getReviewSettingDependentFilters = (reviewSettings, settingId) => {
  const setting = reviewSettings?.find((sett) => `${sett.id}` === `${settingId}`)
  const reviewPeriod = `${getPeriodInterval[setting?.review_period] ?? '1'}`

  return {
    review_setting: settingId != null && settingId !== '' ? `${settingId}` : null,
    review_period: reviewPeriod,
    quarter: getCurrentPeriodDate(reviewPeriod)
  }
}

export const getReviewTypeDependentFilters = (reviewSettings, periodValue, currentSettingId) => {
  const matchingSettings = reviewSettings?.filter(
    (sett) => `${getPeriodInterval[sett?.review_period]}` === `${periodValue}`
  )
  const currentMatches = matchingSettings?.some((sett) => `${sett.id}` === `${currentSettingId}`)
  const settingId = currentMatches
    ? `${currentSettingId}`
    : checkArrayPresence(matchingSettings)
      ? `${matchingSettings[0].id}`
      : null

  return {
    review_setting: settingId,
    review_period: periodValue,
    quarter: getCurrentPeriodDate(`${periodValue}`)
  }
}

export const goalAndKpiFilters = (allFilters) => {
  let newFilters = [
    {
      label: 'Review Settings',
      value: 'review_setting',
      notClear: true,
      component: 'selectWrapper',
      placeholder: 'Select Review Settings',
      ...(allFilters?.reviewSettings
        ? {
            onFilterChange: (value, searchParams) => {
              const dependentFilters = getReviewSettingDependentFilters(allFilters.reviewSettings, value)
              allFilters.setReviewPeriod?.(dependentFilters.review_period)
              return { ...searchParams, ...dependentFilters }
            }
          }
        : {})
    }
  ]

  if (!allFilters?.hideAssignee) {
    newFilters.push({
      label: 'Assignee',
      value: 'assignee',
      userSearchable: true,
      component: 'selectWrapper',
      placeholder: 'Select Status'
    })
  }
  if (allFilters) {
    newFilters.splice(0, 0, {
      label: 'Review Type',
      value: 'review_period',
      nestedSelection: true,
      notClear: true,
      component: 'selectWrapper',
      placeholder: 'Select Review Type',
      selectedValue: allFilters.reviewPeriod,
      setValue: allFilters.setReviewPeriod,
      onFilterChange: (value, searchParams) => {
        const dependentFilters = getReviewTypeDependentFilters(
          allFilters.reviewSettings,
          value,
          searchParams.review_setting
        )
        return { ...searchParams, ...dependentFilters }
      },
      nestedOptions: [
        {
          label: TENURE_FILTER_LABEL,
          value: 'quarter',
          notClear: true,
          component: 'selectWrapper',
          placeholder: `Select ${TENURE_FILTER_LABEL}`,
          conditionToRender: true
        }
      ]
    })
  } else {
    newFilters.splice(0, 0, {
      label: TENURE_FILTER_LABEL,
      value: 'quarter',
      notClear: true,
      component: 'selectWrapper',
      placeholder: `Select ${TENURE_FILTER_LABEL}`
    })
    newFilters.splice(2, 0, {
      label: 'Review Type',
      value: 'review_period',
      notClear: true,
      component: 'selectWrapper',
      placeholder: 'Select Review Type'
    })
  }
  return newFilters
}

export const getAreaWeightage = (area, currentGoal) => {
  let totalWeightage = 0
  area.impact_area_goal_settings.map((area) => area.id !== currentGoal && (totalWeightage += area.weightage))
  return totalWeightage
}

export const getGoalReviewWeightage = (area) => {
  let totalWeightage = 0
  area.goals.map((area) => (totalWeightage += area.weight))
  return totalWeightage
}

export const userCanAddKpiToGoal = (goalTitle, isAssigneeView) => {
  if (goalTitle === 'Values') return false
  if (!isAssigneeView) return true

  return goalTitle === 'Objectives'
}

export const getInitialGoalValues = (type, values) => {
  const result =
    type === 'goal'
      ? {
          id: values.id,
          weight: values.weightage,
          goal_id: values.goal.id,
          kpis: checkArrayPresence(values.goal.goal_setting_kpis)
            ? values.goal.goal_setting_kpis.map((kpi) => {
                return {
                  id: kpi.id,
                  kpi_id: { label: kpi.kpi.title, value: kpi.kpi.id },
                  kpi: { created_by_id: kpi.kpi.created_by_id, from_library: kpi.kpi.from_library },
                  kpi_description: kpi.kpi.description,
                  weightage: kpi.weightage,
                  assign_to: kpi.assign_to_team ? 'team' : 'individual',
                  users: !kpi.assign_to_team ? kpi.user_kpis.map((user) => user.user.id) : undefined,
                  user_kpis: kpi.user_kpis,
                  can_delete: kpi.can_delete,
                  can_edit: kpi.can_edit
                }
              })
            : []
        }
      : {
          kpis: checkArrayPresence(values.goal.goal_setting_kpis)
            ? values.goal.goal_setting_kpis.map((kpi) => {
                const mappedKpi = {
                  id: kpi.id,
                  kpi_id: { label: kpi.kpi.title, value: kpi.kpi.id },
                  kpi: { created_by_id: kpi.kpi.created_by_id, from_library: kpi.kpi.from_library },
                  kpi_description: kpi.kpi.description,
                  weightage: kpi.weightage,
                  assign_to: kpi.assign_to_team ? 'team' : 'individual',
                  users: !kpi.assign_to_team ? kpi.user_kpis.map((user) => user.user.id) : undefined,
                  user_kpis: kpi.user_kpis,
                  can_delete: kpi.can_delete,
                  can_edit: kpi.can_edit
                }
                return mappedKpi
              })
            : []
        }

  return result
}

export const goalAssignedToOptions = [
  { label: 'Individual', value: 'individual' },
  { label: 'Team', value: 'team' }
]

export const ratingScaleValues = [
  {
    label: 'Exceeds Expectations',
    stars: 3,
    content:
      "Performance consistently exceeded expectations in all essential areas of responsibility and the quality of work overall was excellent. Annual objectives were met and can be benchmarked across in both 'What' and 'How' part of the Job."
  },
  {
    label: 'Meets Expectations',
    stars: 2,
    content:
      'Performance consistently met expectations in all essential areas of responsibility, at times possibly exceeding expectations and the quality of work overall was very good. The most critical annual goals were met.'
  },
  {
    label: 'Below Expectation',
    stars: 1,
    content:
      'Performance did not consistently meet expectations – performance failed to meet expectations in one or more essential areas of responsibility and/or one or more of the most critical goals were not met. A performance improvement plan to improve performance must be outlined.'
  }
]

export const initialValues = (impactArea, goal, kpi, drawerAction) => {
  if (drawerAction?.type === 'values_kpis') {
    const activeKpis = (goal?.kpis || []).filter((libraryKpi) => !libraryKpi.archived)

    return {
      kpis_attributes: checkArrayPresence(activeKpis)
        ? activeKpis.map((libraryKpi) => ({
            id: libraryKpi.id,
            title: libraryKpi.title,
            weightage: libraryKpi.weightage,
            description: libraryKpi.description,
            tags:
              libraryKpi.library_tags?.map((libraryTag) => ({
                value: libraryTag.tag.id,
                label: libraryTag.tag.name
              })) || []
          }))
        : []
    }
  }

  if (kpi || drawerAction.type === 'kpi') {
    return {
      title: kpi?.title,
      weightage: kpi?.weightage,
      description: kpi?.description,
      tags: kpi?.library_tags?.map((libraryTag) => ({ value: libraryTag.tag.id, label: libraryTag.tag.name }))
    }
  } else if (goal || drawerAction.type === 'goal') {
    return {
      title: goal?.title,
      weightage: goal?.weightage,
      description: goal?.description,
      tags: goal?.library_tags?.map((libraryTag) => ({ value: libraryTag.tag.id, label: libraryTag.tag.name }))
    }
  } else if (impactArea) {
    return {
      title: impactArea.title,
      weightage: impactArea.weightage,
      state: impactArea.state,
      tags: impactArea.tags_data?.map((libraryTag) => ({ value: libraryTag.tag.id, label: libraryTag.tag.name }))
    }
  } else {
    return {
      title: '',
      weightage: '0',
      state: null,
      tags: []
    }
  }
}

export const getCombinedGoalsInitialValues = (impactArea) => {
  if (!impactArea) return undefined

  return {
    goals_attributes: impactArea.goal_data.map((goal) => ({
      id: goal.id,
      title: goal.title,
      description: goal.description,
      weightage: goal.weightage,
      tags:
        goal.library_tags?.map((libraryTag) => ({
          value: libraryTag.tag.id,
          label: libraryTag.tag.name
        })) || []
    }))
  }
}

export const getCombinedGoalsSubmitValues = (values, impactArea) => {
  const goalsById = new Map(impactArea.goal_data.map((goal) => [goal.id, goal]))

  return values.goals_attributes.map((goal) => {
    const previousGoal = goalsById.get(goal.id)
    const previousTags = previousGoal?.library_tags || []
    const selectedTagNames = (goal.tags || []).map((tag) => tag.label)
    const previousTagNames = previousTags.map((tag) => tag.tag.name)

    return {
      id: goal.id,
      description: goal.description.trim(),
      weightage: Number(goal.weightage),
      library_tags_attributes: [
        ...previousTags.map((libraryTag) => ({
          id: libraryTag.id,
          tag_name: libraryTag.tag.name,
          ...(!selectedTagNames.includes(libraryTag.tag.name) && { _destroy: true })
        })),
        ...(goal.tags || [])
          .filter((tag) => !previousTagNames.includes(tag.label))
          .map((tag) => ({ tag_name: tag.label }))
      ]
    }
  })
}

export const stateOptions = [
  {
    label: 'Qualitative',
    value: 'qualitative'
  },
  {
    label: 'Quantitative',
    value: 'quantitative'
  }
]

const hasLibraryKpiChanged = (item, previousKpi) => {
  if (!item?.id || !previousKpi) return true

  const nextWeightage = item?.archived ? 0 : Number(item?.weightage)
  const archivedChanged = !!item?.archived !== !!previousKpi?.archived
  const weightageChanged = nextWeightage !== Number(previousKpi?.weightage)

  return archivedChanged || weightageChanged
}

export const formatFormValues = (formValues, impactArea, goal, kpi, type) => {
  if (type === 'values_kpis') {
    const kpis_attributes = formValues?.kpis_attributes || []
    const existingKpisById = new Map((goal?.kpis || []).map((libraryKpi) => [libraryKpi.id, libraryKpi]))
    const kpisChanged = kpis_attributes.some((item) => hasLibraryKpiChanged(item, existingKpisById.get(item?.id)))

    return {
      id: goal.id,
      kpis_changed: kpisChanged,
      kpis_attributes: kpis_attributes.map((item) => {
        const prevLibraryTags = goal?.kpis?.find((kpi) => kpi.id === item?.id)?.library_tags
        const library_tags_attributes =
          item?.id && prevLibraryTags
            ? getLibraryTagsFormValues({ tags: item.tags || [] }, prevLibraryTags) ||
              (item.tags || []).map((tag) => ({ tag_name: tag.label }))
            : (item.tags || []).map((tag) => ({ tag_name: tag.label }))

        return {
          id: item?.id,
          title: item?.title,
          weightage: item?.archived ? 0 : item?.weightage,
          description: item?.description,
          archived: !!item?.archived,
          library_tags_attributes,
          from_library: true
        }
      })
    }
  }

  formValues.library_tags_attributes = formValues.tags?.map((tag) => ({ tag_name: tag.label }))
  if (kpi || type === 'kpi') {
    const title = formValues.title
    delete formValues.title

    formValues.library_tags_attributes = kpi
      ? getLibraryTagsFormValues(formValues, kpi.library_tags)
      : formValues.library_tags_attributes
    formValues.id = goal.id
    formValues.kpis_changed = !kpi?.id || Number(formValues?.weightage) !== Number(kpi?.weightage)
    formValues.kpis_attributes = [
      {
        id: kpi?.id,
        title: title,
        library_tags_attributes: formValues.library_tags_attributes,
        weightage: formValues?.weightage,
        description: formValues?.description,
        from_library: true
      }
    ]
    delete formValues.library_tags_attributes
    delete formValues.weightage
    delete formValues.description

    return formValues
  } else if (goal || type === 'goal') {
    formValues.id = goal?.id
    formValues.library_tags_attributes = getLibraryTagsFormValues(formValues, goal?.library_tags)
    if (formValues.kpis_attributes?.some((item) => !item?.id)) {
      formValues.kpis_changed = true
    }
  } else if (impactArea) {
    formValues.library_tags_attributes = getLibraryTagsFormValues(formValues, impactArea.tags_data)
  }

  return formValues
}

const getLibraryTagsFormValues = (formValues, prevTags = []) => {
  let selectedTags = formValues.tags?.map((tag) => tag.label)
  if (!selectedTags) return

  let prev_tags = prevTags?.map((tag) => ({ id: tag.id, tag_name: tag.tag.name }))
  let prevselectedTags = prev_tags.map((tag) => tag.tag_name)
  let newTags = selectedTags.filter((val) => !prevselectedTags.includes(val))

  formValues.library_tags_attributes = prev_tags.map((tag) => {
    if (!selectedTags.includes(tag.tag_name)) {
      return { _destroy: true, id: tag.id, tag_name: tag.tag_name }
    } else {
      return { id: tag.id, tag_name: tag.tag_name }
    }
  })

  formValues.library_tags_attributes = formValues.library_tags_attributes.concat(
    newTags.map((tag) => ({ tag_name: tag }))
  )

  return formValues.library_tags_attributes
}

export const reviewPeriodOptions = [
  { label: 'Month', value: '0' },
  { label: 'Quarter', value: '1' },
  { label: 'Bi-Annual', value: '2' },
  { label: 'Annual', value: '3' }
]

export const reviewerTypeOptions = [
  { label: 'Approver', value: 'approver' },
  { label: 'Reviewer', value: 'reviewer' },
  { label: 'People Excellence Executive', value: 'pse' }
]

export const getReviewerTypeOptions = (isPseUser = false) => {
  const options = [
    { label: 'Approver', value: 'approver' },
    { label: 'Reviewer', value: 'reviewer' }
  ]
  if (isPseUser) options.push({ label: 'People Excellence Executive', value: 'pse' })
  return options
}

export const getCurrentQuarterDate = (date) => {
  const quarter = moment(date).quarter()
  return `1-${quarter === 2 ? '4' : quarter === 3 ? '7' : quarter === 4 ? '10' : quarter}-${getYearFromDate(date)}`
}

export const monthOptions = {
  0: 'Jan',
  1: 'Feb',
  2: 'Mar',
  3: 'Apr',
  4: 'May',
  5: 'Jun',
  6: 'Jul',
  7: 'Aug',
  8: 'Sep',
  9: 'Oct',
  10: 'Nov',
  11: 'Dec'
}

export const getQuarterOptions = (type) => {
  const currentYear = getYearFromDate(new Date())
  if (type === '0') {
    return [
      ...Array(24)
        .fill(0)
        .map((_, index) => ({
          label: `${monthOptions[index % 12]} ${index > 11 ? currentYear + 1 : currentYear}`,
          value: `1-${(index % 12) + 1}-${index > 11 ? currentYear + 1 : currentYear}`
        }))
    ]
  } else if (type === '1') {
    return [
      { label: `Q1 ${currentYear}`, value: `1-1-${currentYear}` },
      { label: `Q2 ${currentYear}`, value: `1-4-${currentYear}` },
      { label: `Q3 ${currentYear}`, value: `1-7-${currentYear}` },
      { label: `Q4 ${currentYear}`, value: `1-10-${currentYear}` },
      { label: `Q1 ${currentYear + 1}`, value: `1-1-${currentYear + 1}` },
      { label: `Q2 ${currentYear + 1}`, value: `1-4-${currentYear + 1}` },
      { label: `Q3 ${currentYear + 1}`, value: `1-7-${currentYear + 1}` },
      { label: `Q4 ${currentYear + 1}`, value: `1-10-${currentYear + 1}` }
    ]
  } else if (type === '2') {
    return [
      { label: `Apr - Sep ${currentYear}`, value: biannualAprilPeriodStartValue(currentYear) },
      {
        label: `Oct ${currentYear} - Mar ${currentYear + 1}`,
        value: biannualOctoberPeriodStartValue(currentYear)
      },
      { label: `Apr - Sep ${currentYear + 1}`, value: biannualAprilPeriodStartValue(currentYear + 1) },
      {
        label: `Oct ${currentYear + 1} - Mar ${currentYear + 2}`,
        value: biannualOctoberPeriodStartValue(currentYear + 1)
      }
    ]
  } else {
    return [
      { label: `Apr ${currentYear} - Mar ${currentYear + 1}`, value: `1-4-${currentYear}` },
      { label: `Apr ${currentYear + 1} - Mar ${currentYear + 2}`, value: `1-4-${currentYear + 1}` }
    ]
  }
}

export const getTotalWeightageSum = (impactAreas) => {
  const add = (accumulator, a) => accumulator + a
  return impactAreas.map((impactArea) => impactArea.weightage).reduce(add, 0)
}

export const highgligtSearchedText = (title, searchedText) =>
  title.replace(searchedText, `<span class='highlight'> ${searchedText} </span>`)

const quarterOptions = { 0: 'month', 1: 'quarter', 3: 'year' }

const getPreviousPeriodEnd = (date) => date.subtract(1, 'months').endOf('month').format(TWO_DIGIT_FORMAT)

const biannualPeriodBounds = (momentDate) => {
  const month = momentDate.month() + 1
  const year = momentDate.year()
  if (isInBiannualAprilPeriod(month)) {
    return [moment([year, BIANNUAL_APRIL_MONTH - 1, 1]), moment([year, BIANNUAL_SEPTEMBER_MONTH - 1, 30]).endOf('day')]
  }
  if (month >= BIANNUAL_OCTOBER_MONTH) {
    return [
      moment([year, BIANNUAL_OCTOBER_MONTH - 1, 1]),
      moment([year + 1, BIANNUAL_MARCH_MONTH - 1, 31]).endOf('day')
    ]
  }
  return [moment([year - 1, BIANNUAL_OCTOBER_MONTH - 1, 1]), moment([year, BIANNUAL_MARCH_MONTH - 1, 31]).endOf('day')]
}

export const biannualTimelineLabel = (startDate) => {
  const date = moment(startDate)
  const month = date.month() + 1
  const year = date.year()
  if (isInBiannualAprilPeriod(month)) {
    return `Apr - Sep ${year}`
  }
  if (month >= BIANNUAL_OCTOBER_MONTH) {
    return `Oct ${year} - Mar ${year + 1}`
  }
  return `Oct ${year - 1} - Mar ${year}`
}

export const annualTimelineLabel = (startDate) => {
  const startYear = fiscalYearStartYear(startDate)
  return `Apr ${startYear} - Mar ${startYear + 1}`
}

const fiscalYearBounds = (momentDate) => {
  const startYear = fiscalYearStartYear(momentDate.toDate())
  return [moment([startYear, 3, 1]), moment([startYear + 1, 2, 31]).endOf('day')]
}

const isInCurrentPeriod = (currentDate, date, type) => {
  if (type === '2') {
    const [biAnnualStart, biAnnualEnd] = biannualPeriodBounds(currentDate)
    return date.isBetween(biAnnualStart, biAnnualEnd, null, '[]')
  } else if (type === '3') {
    const [fiscalStart, fiscalEnd] = fiscalYearBounds(currentDate)
    return date.isBetween(fiscalStart, fiscalEnd, null, '[]')
  }
  return currentDate.isSame(date, quarterOptions[type]) && currentDate.isSame(date, 'year')
}

export const getEffectiveTillDate = (date, type, fromDate, id, timeZone = 'Asia/Karachi') => {
  const quarterDate = moment(date, PMS_DATE_FORMAT)
  const currentDate = moment()
  const effectiveFromDate = moment(formatDate(fromDate, timeZone, TWO_DIGIT_FORMAT))

  const isSameDate = effectiveFromDate.isSame(
    quarterDate,
    type === '0' ? 'month' : type === '1' ? 'quarter' : type === '2' ? 'year' : 'year'
  )
  const isInCurrentPeriodFlag = isInCurrentPeriod(currentDate, quarterDate, type)

  if (!isSameDate && !isInCurrentPeriodFlag) {
    return {
      id,
      effective_till: moment(getPreviousPeriodEnd(quarterDate.clone()), TWO_DIGIT_FORMAT).format(PMS_DATE_FORMAT)
    }
  } else {
    return { id, _destroy: true }
  }
}

export const getDeletedValues = (date, type, fromDate, goalId, setting) => ({
  id: setting.id,
  reviewer_impact_areas_attributes: setting.reviewer_impact_areas.map((area) => ({
    id: area.id,
    impact_area_goal_settings_attributes: area.impact_area.impact_area_goal_settings.map((goal) => ({
      ...(goalId === goal.id ? getEffectiveTillDate(date, type, fromDate, goalId) : { id: goal.id }),
      goal_setting_kpis_attributes: goal.goal.goal_setting_kpis.map((kpi) => ({
        ...(goalId === goal.id ? getEffectiveTillDate(date, type, kpi.effective_from, kpi.id) : { id: kpi.id }),
        ...(!kpi.assign_to_team && {
          user_kpis_attributes: kpi.user_kpis.map((user) => ({
            user_id: user.user.id,
            ...(goalId === goal.id ? getEffectiveTillDate(date, type, user.effective_from, user.id) : { id: user.id })
          }))
        })
      }))
    }))
  }))
})

export const getEffectiveFromDate = (date, timeZone) => {
  const quarterDate = moment(
    moment(formatDate(date, timeZone, 'D-M-YYYY'), TWO_DIGIT_FORMAT).format(PMS_DATE_FORMAT),
    PMS_DATE_FORMAT
  )
  return moment(quarterDate).format(PMS_DATE_FORMAT)
}

export const getUpdatedUserValues = (date, type, setting, currentKpi, newUsers, timeZone) => ({
  id: setting.id,
  reviewer_impact_areas_attributes: setting.reviewer_impact_areas.map((area) => ({
    id: area.id,
    impact_area_goal_settings_attributes: area.impact_area.impact_area_goal_settings.map((goal) => ({
      id: goal.id,
      goal_setting_kpis_attributes: goal.goal.goal_setting_kpis.map((kpi) => ({
        id: kpi.id,
        assign_to_team: kpi.assign_to_team,
        ...(!kpi.assign_to_team &&
          (kpi.id === currentKpi.id
            ? {
                user_kpis_attributes: [
                  ...currentKpi.user_kpis.map((user) =>
                    newUsers.includes(user.user.id)
                      ? { id: user.id, user_id: user.user.id }
                      : getEffectiveTillDate(date, type, user.effective_from, user.id)
                  ),
                  ...newUsers
                    .filter((userId) => !currentKpi.user_kpis.some((oldUser) => oldUser.user.id === userId))
                    .map((user) => ({
                      user_id: user,
                      effective_from: getEffectiveFromDate(date, timeZone)
                    }))
                ]
              }
            : {
                user_kpis_attributes: kpi.user_kpis.map((user) => ({
                  id: user.id,
                  user_id: user.user.id
                }))
              }))
      }))
    }))
  }))
})

const getKpiDates = (prevKpi, quarter) =>
  prevKpi && formatDate(prevKpi?.effective_from, PMS_DATE_FORMAT) !== formatDate(quarter, TWO_DIGIT_FORMAT)

const normalizeKpiSelectValue = (kpiIdField) => {
  if (kpiIdField == null || kpiIdField === '') return null

  if (typeof kpiIdField === 'object') {
    return kpiIdField.value ?? null
  }

  return kpiIdField
}

const isNewKpiSelectValue = (kpiIdField) => {
  const value = normalizeKpiSelectValue(kpiIdField)
  if (value == null || value === '') return false

  return typeof value !== 'number' && !Number.isInteger(Number(value))
}

const buildKpiIdentityAttributes = (kpi, weightageTransform = (w) => w ?? 0) => {
  const rawKpiId = kpi?.kpi_id
  if (rawKpiId == null || rawKpiId === '') return null

  if (isNewKpiSelectValue(rawKpiId)) {
    return {
      kpi_title: typeof rawKpiId === 'object' ? (rawKpiId.label ?? rawKpiId.value) : rawKpiId,
      kpi_description: kpi.kpi_description,
      weightage: weightageTransform(kpi.weightage)
    }
  }

  const kpiIdValue = normalizeKpiSelectValue(rawKpiId)
  if (kpiIdValue == null || kpiIdValue === '') return null

  return {
    kpi_id: Number(kpiIdValue),
    weightage: weightageTransform(kpi.weightage),
    ...(kpi.kpi_description !== undefined && { kpi_description: kpi.kpi_description })
  }
}

const getGoalSettingKpisAttributes = (kpis, quarter, timeZone) => {
  if (!checkArrayPresence(kpis)) {
    return []
  }

  return kpis
    .map((kpi) => {
      if (kpi._destroy && kpi.id) {
        return {
          id: kpi.id,
          _destroy: true
        }
      }

      if (kpi.id) {
        return {
          id: kpi.id,
          ...(kpi.weightage !== undefined && { weightage: kpi.weightage }),
          ...(kpi.kpi_description !== undefined && { kpi_description: kpi.kpi_description })
        }
      }

      const identityAttributes = buildKpiIdentityAttributes(kpi)
      if (!identityAttributes) return null

      const assignToTeam = kpi.assign_to === 'team'

      return {
        ...(quarter && timeZone && { effective_from: getEffectiveFromDate(quarter, timeZone) }),
        ...identityAttributes,
        assign_to_team: assignToTeam,
        ...(!assignToTeam && {
          user_kpis_attributes: checkArrayPresence(kpi.users)
            ? kpi.users.map((user) => ({
                user_id: user,
                ...(quarter && timeZone && { effective_from: getEffectiveFromDate(quarter, timeZone) })
              }))
            : []
        })
      }
    })
    .filter((kpi) => kpi && Object.keys(kpi).length > 0)
}

const getUserValues = (kpis, prevKpis, quarter, type, timeZone) => {
  let newKpis = []
  if (checkArrayPresence(kpis)) {
    kpis.forEach((kpi) => {
      if (kpi._destroy && kpi.id) {
        newKpis.push({
          id: kpi.id,
          _destroy: true
        })
        return
      }

      if (
        getKpiDates(
          prevKpis.find((oldKpi) => oldKpi.id === kpi.id),
          quarter
        )
      ) {
        const prevKpi = prevKpis.find((oldKpi) => oldKpi.id === kpi.id)
        newKpis.push({
          kpi_id: prevKpi.kpi.id,
          assign_to_team: prevKpi.assign_to_team,
          ...getEffectiveTillDate(quarter, type, prevKpi.effective_from, prevKpi.id),
          ...(!prevKpi.assign_to_team && {
            user_kpis_attributes: [
              ...(checkArrayPresence(prevKpi.user_kpis)
                ? prevKpi.user_kpis.map((user) => ({
                    user_id: user.user.id,
                    ...getEffectiveTillDate(quarter, type, user.effective_from, user.id)
                  }))
                : [])
            ]
          })
        })
        const isNewKpi = isNewKpiSelectValue(kpi.kpi_id)
        newKpis.push({
          effective_from: getEffectiveFromDate(quarter, timeZone),
          ...(isNewKpi
            ? {
                kpi_title: kpi.kpi_id?.label ?? normalizeKpiSelectValue(kpi.kpi_id),
                kpi_description: kpi.kpi_description,
                weightage: kpi.weightage
              }
            : {
                kpi_id: Number(normalizeKpiSelectValue(kpi.kpi_id)),
                ...(kpi.weightage !== undefined && { weightage: kpi.weightage }),
                kpi_description: kpi.kpi_description
              }),
          assign_to_team: kpi.assign_to === 'team',
          ...(kpi.assign_to !== 'team' && {
            user_kpis_attributes: [
              ...(checkArrayPresence(kpi.users)
                ? kpi.users.map((user) => ({
                    user_id: user,
                    effective_from: getEffectiveFromDate(quarter, timeZone)
                  }))
                : [])
            ]
          })
        })
      } else {
        const identityAttributes = buildKpiIdentityAttributes(kpi)
        if (!identityAttributes) return

        const assignToTeam = kpi.assign_to !== undefined ? kpi.assign_to === 'team' : false

        newKpis.push({
          ...(kpi.id ? { id: kpi.id } : { effective_from: getEffectiveFromDate(quarter, timeZone) }),
          ...identityAttributes,
          assign_to_team: assignToTeam,
          ...(!assignToTeam && {
            user_kpis_attributes: [
              ...(prevKpis.find((oldKpi) => oldKpi.id === kpi.id)
                ? prevKpis
                    .find((oldKpi) => oldKpi.id === kpi.id)
                    .user_kpis.map((user) =>
                      kpi.users?.includes(user.user.id)
                        ? {
                            id: user.id,
                            user_id: user.user.id
                          }
                        : getEffectiveTillDate(quarter, type, user.effective_from, user.id)
                    )
                : []),
              ...(prevKpis.find((oldKpi) => oldKpi.id === kpi.id)
                ? checkArrayPresence(kpi.users)
                  ? kpi.users
                      .filter(
                        (user) =>
                          !prevKpis
                            .find((oldKpi) => oldKpi.id === kpi.id)
                            ?.user_kpis.some((oldUser) => oldUser.user.id === user)
                      )
                      .map((user) => ({
                        user_id: user,
                        effective_from: getEffectiveFromDate(quarter, timeZone)
                      }))
                  : []
                : checkArrayPresence(kpi.users)
                  ? kpi.users.map((user) => ({
                      user_id: user,
                      effective_from: getEffectiveFromDate(quarter, timeZone)
                    }))
                  : [])
            ]
          })
        })
      }
    })
  }
  return newKpis
}

export const getFormSubmitValues = (settings, values, quarter, type, timeZone) => {
  const currentSetting = settings.setting
  const isSingleKpiEdit = settings.action === 'edit' && settings.type === 'kpi'
  const kpiWeightage = (weightage) => (settings.isAssigneeView ? 0 : weightage || 0)

  // For single KPI edits, only include the area and goal being edited
  let newValues = {
    id: currentSetting.id,
    reviewer_impact_areas_attributes: isSingleKpiEdit
      ? [
          {
            id: currentSetting.reviewer_impact_areas.find((area) => area.id === settings.areaId)?.id,
            impact_area_goal_settings_attributes: [
              {
                id: currentSetting.reviewer_impact_areas
                  .find((area) => area.id === settings.areaId)
                  ?.impact_area.impact_area_goal_settings.find((goal) => goal.id === settings.goalId)?.id,
                goal_setting_kpis_attributes: [] // Will be populated below
              }
            ]
          }
        ]
      : currentSetting.reviewer_impact_areas.map((area) => ({
          id: area.id,
          impact_area_goal_settings_attributes: area.impact_area.impact_area_goal_settings.map((goal) => ({
            id: goal.id,
            goal_setting_kpis_attributes: goal.goal.goal_setting_kpis.map((kpi) => ({
              id: kpi.id,
              ...(kpi.assign_to_team
                ? { assign_to_team: kpi.assign_to_team }
                : {
                    user_kpis_attributes: kpi.user_kpis.map((user) => ({
                      id: user.id,
                      user_id: user.user.id
                    }))
                  })
            }))
          }))
        }))
  }
  const areaIndex = newValues.reviewer_impact_areas_attributes.findIndex((area) => area.id === settings.areaId)
  if (settings.action === 'new') {
    const goalIndex = settings.goalId
      ? newValues.reviewer_impact_areas_attributes[areaIndex].impact_area_goal_settings_attributes.findIndex(
          (goal) => goal.id === settings.goalId
        )
      : null
    if (settings.type === 'goal') {
      newValues.reviewer_impact_areas_attributes[areaIndex].impact_area_goal_settings_attributes.push({
        goal_id: values.goal_id,
        weightage: values.weight,
        effective_from: getEffectiveFromDate(quarter, timeZone),
        goal_setting_kpis_attributes: checkArrayPresence(values.kpis)
          ? values.kpis
              .map((kpi) => {
                const identityAttributes = buildKpiIdentityAttributes(kpi, kpiWeightage)
                if (!identityAttributes) return null

                return {
                  ...identityAttributes,
                  effective_from: getEffectiveFromDate(quarter, timeZone),
                  assign_to_team: kpi.assign_to === 'team',
                  ...(kpi.assign_to !== 'team' && {
                    user_kpis_attributes: checkArrayPresence(kpi.users)
                      ? kpi.users.map((user) => ({
                          user_id: user,
                          effective_from: getEffectiveFromDate(quarter, timeZone)
                        }))
                      : []
                  })
                }
              })
              .filter(Boolean)
          : []
      })
    } else {
      values.kpis.forEach((kpi) => {
        const identityAttributes = buildKpiIdentityAttributes(kpi, kpiWeightage)
        if (!identityAttributes) return

        newValues.reviewer_impact_areas_attributes[areaIndex].impact_area_goal_settings_attributes[
          goalIndex
        ].goal_setting_kpis_attributes.push({
          ...identityAttributes,
          effective_from: moment(formatDate(quarter, timeZone, 'D-M-YYYY'), TWO_DIGIT_FORMAT).format(PMS_DATE_FORMAT),
          assign_to_team: kpi.assign_to === 'team',
          ...(kpi.assign_to !== 'team' && {
            user_kpis_attributes: checkArrayPresence(kpi.users)
              ? kpi.users.map((user) => ({
                  user_id: user,
                  effective_from: moment(formatDate(quarter, timeZone, 'D-M-YYYY'), TWO_DIGIT_FORMAT).format(
                    PMS_DATE_FORMAT
                  )
                }))
              : []
          })
        })
      })
    }
  } else {
    const goalIndex = newValues.reviewer_impact_areas_attributes[
      areaIndex
    ].impact_area_goal_settings_attributes.findIndex(
      (goal) => goal.id === (settings.type === 'kpi' ? settings.goalId : values.id)
    )
    const mainGoal = currentSetting.reviewer_impact_areas[areaIndex].impact_area.impact_area_goal_settings[goalIndex]

    // When editing a single KPI, only include that KPI. When editing a goal, include all KPIs and mark removed ones.
    const isSingleKpiEdit = settings.type === 'kpi'
    const removedKpis = isSingleKpiEdit
      ? [] // Don't mark other KPIs as removed when editing a single KPI
      : mainGoal.goal.goal_setting_kpis.filter((oldKpi) => !values.kpis.some((kpi) => kpi.id === oldKpi.id))
    const prevKpis = mainGoal.goal.goal_setting_kpis.filter((oldKpi) => values.kpis.some((kpi) => kpi.id === oldKpi.id))

    // For single KPI edit, only send id and weightage
    const singleKpiUpdate =
      isSingleKpiEdit && values.kpis?.[0]?.id
        ? settings.isAssigneeView
          ? [
              {
                id: values.kpis[0].id,
                kpi_description: values.kpis[0].kpi_description
              }
            ]
          : [
              {
                id: values.kpis[0].id,
                ...(values.kpis[0].weightage && { weightage: values.kpis[0].weightage }),
                kpi_description: values.kpis[0].kpi_description
              }
            ]
        : null

    newValues.reviewer_impact_areas_attributes[areaIndex].impact_area_goal_settings_attributes[goalIndex] = {
      id: values.id ?? settings.goalId,
      ...(settings.type === 'goal' && { goal_id: values.goal_id, weightage: values.weight }),
      goal_setting_kpis_attributes:
        singleKpiUpdate ||
        (isSingleKpiEdit
          ? getUserValues(values.kpis, prevKpis, quarter, type, timeZone) // Fallback if no id
          : [
              ...removedKpis.map((kpi) => ({
                ...getEffectiveTillDate(quarter, type, kpi.effective_from, kpi.id),
                assign_to_team: kpi.assign_to_team,
                ...(!kpi.assign_to_team && {
                  user_kpis_attributes: checkArrayPresence(kpi.user_kpis)
                    ? kpi.user_kpis.map((user) => ({
                        ...getEffectiveTillDate(quarter, type, user.effective_from, user.id)
                      }))
                    : []
                })
              })),
              ...getGoalSettingKpisAttributes(values.kpis, quarter, timeZone)
            ])
    }
  }
  return newValues
}

export const getSortedReviewsHash = (reviews) => {
  const initialReviewStructure = {
    monthly: [],
    quarterly: [],
    biannual: [],
    annual: []
  }
  return reviews.reduce((acc, current) => {
    const { year, review_type } = current
    if (!acc[year]) {
      acc[year] = { ...initialReviewStructure }
    }

    acc[year][review_type].push(current)

    return acc
  }, {})
}

export const getPeriodInterval = {
  monthly: '0',
  quarterly: '1',
  biannual: '2',
  annual: '3'
}

export const getInitialParams = (settings, setReviewPeriod) => {
  const values = checkArrayPresence(settings) ? settings[0] : null
  const path = window.location.search
  const review_period = path.includes('review_period=')
    ? path.split('review_period=')[1].split('&')[0]
    : values
      ? `${getPeriodInterval[values.review_period]}`
      : '1'

  setReviewPeriod(review_period)

  return {
    quarter: path.includes('quarter=')
      ? path.split('quarter=')[1].split('&')[0]
      : defaultQuarterForReviewPeriod(review_period),
    review_period,
    review_setting: path.includes('review_setting=')
      ? path.split('review_setting=')[1].split('&')[0]
      : values
        ? `${values.id}`
        : null
  }
}

export const filterCommentsForThread = (apiComments, commentableId, excludeCommentIds = []) => {
  if (!checkArrayPresence(apiComments)) return []

  let filtered = apiComments

  if (commentableId) {
    const byCommentable = apiComments.filter((comment) => comment.commentable_id === commentableId)
    if (byCommentable.length > 0) filtered = byCommentable
  }

  if (checkArrayPresence(excludeCommentIds)) {
    const excludeIds = new Set(excludeCommentIds)
    filtered = filtered.filter((comment) => !excludeIds.has(comment.id))
  }

  return filtered
}

export const mapApiCommentsToThread = (apiComments) => ({
  unread_count: 0,
  messages: checkArrayPresence(apiComments)
    ? apiComments.map((message) => ({
        id: message.id,
        time: message.created_at,
        read: true,
        text: message.comment,
        attachments: message.comment_attachments,
        commentable_id: message.commentable_id,
        user: {
          id: message.user_id,
          name: message.username,
          avatar: message.avatar
        }
      }))
    : []
})

export const mapKpiCommentsToThread = (apiComments, commentableId, excludeCommentIds = []) =>
  mapApiCommentsToThread(filterCommentsForThread(apiComments, commentableId, excludeCommentIds))

export const hasKpiCommentContent = (value, attachments) => !!value?.trim() || attachments?.length > 0

export const buildCommentAttributes = (value, attachments) => {
  const attributes = {}

  if (value?.trim()) attributes.comment = value.trim()
  if (attachments?.length > 0) {
    attributes.comment_attachments_attributes = [{ file: attachments[0] }]
  }

  return attributes
}

export const getCommentThreadCount = (comments) => comments?.messages?.length || 0

export const getUserKpiCommentContext = (kpi, userId) => {
  const userKpis = kpi?.user_kpis ?? []
  const matchUserKpi = (uk) => userId != null && Number(uk.user?.id) === Number(userId)
  const userKpi = checkArrayPresence(userKpis) ? userKpis.find(matchUserKpi) : null
  const fallbackUserKpi = userKpi ?? (checkArrayPresence(userKpis) && userKpis.length === 1 ? userKpis[0] : null)
  const userKpiId = fallbackUserKpi?.id ?? kpi.user_kpi_id
  const apiComments = fallbackUserKpi?.comments ?? kpi.comments ?? []

  return { userKpiId, comments: mapKpiCommentsToThread(apiComments, userKpiId) }
}

export const getReviewKpiCommentLiveKey = (syncKpiId) => `review_${syncKpiId}`

export const getUserKpiCommentLiveKey = (userKpiId) => `user_kpi_${userKpiId}`

export const buildReviewKpiResponseIdLookup = (impactAreas) => {
  if (!checkArrayPresence(impactAreas)) return {}

  return impactAreas
    .flatMap((area) => area.goals ?? [])
    .flatMap((goal) => goal.kpis ?? [])
    .reduce((lookup, kpi) => {
      const syncKpiId = kpi.performance_review_user_kpi_id ?? kpi.performance_review_sync_user_kpi_id ?? kpi.id
      lookup[syncKpiId] = kpi.user_kpi_response?.id ?? null
      return lookup
    }, {})
}

export const getReviewKpiResponseId = (impactAreas, performanceReviewUserKpiId) => {
  if (performanceReviewUserKpiId == null) return null

  return buildReviewKpiResponseIdLookup(impactAreas)[Number(performanceReviewUserKpiId)] ?? null
}

export const getReviewFormInitialValues = (values) => {
  if (Object.keys(values).length === 0) return

  const result = {
    reviewer_comment_strength: values.strength_wrt_reviewer,
    reviewer_comment_learning: values.learning_wrt_reviewer,
    user_comment_strength: values.strength_wrt_user,
    user_comment_learning: values.learning_wrt_user,
    discussion_confirmed_by_reviewer: values.discussion_confirmed_by_reviewer,
    impact_areas: checkArrayPresence(values.impact_areas)
      ? values.impact_areas.map((area) => {
          return {
            id: area.id,
            title: area.title,
            state: area.state,
            weight: area.weightage,
            total_employee_rating: area.total_employee_rating,
            total_reviewer_rating: area.total_reviewer_rating,
            goals: checkArrayPresence(area.goals)
              ? area.goals.map((goal) => {
                  const kpis =
                    checkArrayPresence(goal.kpis) && goal.kpis[0]
                      ? goal.kpis.map((kpi) => {
                          const userKpiId = kpi.user_kpi_id ?? kpi.id
                          const apiComments = [...(kpi.user_kpi_response?.comments ?? kpi.comments ?? [])]

                          return {
                            id: kpi.performance_review_user_kpi_id,
                            user_kpi_id: userKpiId,
                            response_id: kpi.user_kpi_response?.id,
                            title: kpi.title,
                            description: kpi.description,
                            employee_rating: kpi.user_kpi_response?.user_rating,
                            reviewer_rating: kpi.user_kpi_response?.reviewer_rating,
                            weightage: kpi.weightage,
                            comments: mapKpiCommentsToThread(apiComments)
                          }
                        })
                      : []
                  return {
                    id: goal.id,
                    title: goal.title,
                    weight: goal.weightage,
                    total_employee_rating: goal.total_employee_rating,
                    total_reviewer_rating: goal.total_reviewer_rating,
                    kpis
                  }
                })
              : []
          }
        })
      : []
  }

  if (typeof window !== 'undefined' && result.impact_areas?.length) {
    const commentRefs = []
    result.impact_areas.forEach((area, ia) => {
      area.goals?.forEach((goal, ig) => {
        goal.kpis?.forEach((kpi, ik) => {
          commentRefs.push({
            path: `ia[${ia}].g[${ig}].k[${ik}]`,
            ref: kpi.comments,
            len: kpi.comments?.messages?.length
          })
        })
      })
    })
  }

  return result
}

export const getUserReviewSubmitValues = (settings, values, quarter, timeZone) => {
  const currentSetting = settings.setting
  let newValues = {
    id: currentSetting.review_setting_id,
    reviewer_impact_areas_attributes: [
      {
        id: currentSetting.id,
        impact_area_goal_settings_attributes: currentSetting.goals.map((goal) => ({
          id: goal.id,
          goal_setting_kpis_attributes: goal.kpis.map((kpi) => ({
            id: kpi.id
          }))
        }))
      }
    ]
  }
  if (settings.type === 'goal') {
    newValues.reviewer_impact_areas_attributes[0].impact_area_goal_settings_attributes.push({
      goal_id: values.goal_id,
      weightage: values.weight,
      effective_from: getEffectiveFromDate(quarter, timeZone),
      goal_setting_kpis_attributes: checkArrayPresence(values.kpis)
        ? values.kpis.map((kpi) => ({
            kpi_id: kpi.kpi_id,
            effective_from: getEffectiveFromDate(quarter, timeZone),
            assign_to_team: kpi.assign_to === 'team',
            ...(kpi.assign_to !== 'team' && {
              user_kpis_attributes: checkArrayPresence(kpi.users)
                ? kpi.users.map((user) => ({
                    user_id: user,
                    effective_from: getEffectiveFromDate(quarter, timeZone)
                  }))
                : []
            })
          }))
        : []
    })
  } else {
    const goalIndex = newValues.reviewer_impact_areas_attributes[0].impact_area_goal_settings_attributes.findIndex(
      (goal) => goal.id === settings.goalId
    )
    values.kpis.map((kpi) => {
      newValues.reviewer_impact_areas_attributes[0].impact_area_goal_settings_attributes[
        goalIndex
      ].goal_setting_kpis_attributes.push({
        kpi_id: kpi.kpi_id,
        effective_from: getEffectiveFromDate(quarter, timeZone),
        assign_to_team: kpi.assign_to === 'team',
        ...(kpi.assign_to !== 'team' && {
          user_kpis_attributes: checkArrayPresence(kpi.users)
            ? kpi.users.map((user) => ({
                user_id: user,
                effective_from: getEffectiveFromDate(quarter, timeZone)
              }))
            : []
        })
      })
    })
  }
  return newValues
}

const formatFileSize = (bytes) => {
  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes === 0) return '0 Bytes'
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + units[i]
}

export const renderChatAttachments = (attachments) =>
  attachments.map((attachment) => (
    <div key={attachment.url ? attachment.url : attachment.media} className="attachment-block">
      <a
        href={attachment.url ? attachment.url : attachment.media}
        target="_blank"
        rel="noreferrer"
        className="d-flex gp-10">
        {fileAttachmentThumbnails(attachment, 'attachment-size')}
        {attachment.name && (
          <div className="flex-column align-items-start justify-space-between">
            <Typography variant="text-sm" type="medium" color="gray-700">
              {formatLongText(attachment.name, 200)}
            </Typography>
            <Typography variant="text-sm" color="gray-500">
              {formatFileSize(attachment.size)}
            </Typography>
          </div>
        )}
      </a>
    </div>
  ))

export const PmsCustomTooltipClass = {
  tooltip: {
    sx: {
      color: darkGrayColor,
      backgroundColor: whiteChipColor
    }
  },
  arrow: {
    sx: {
      color: whiteChipColor
    }
  }
}

export const getTitleFromGoal = (title) => {
  return title === 'Objectives' ? 'Add KPI' : `Add ${title} competencies`
}

export const getHeadingFromGoal = (title) => {
  return title === 'Objectives' ? 'KPI Name' : `${title} competency Name`
}

export const isValuesGoalTitle = (title) => {
  const normalizedTitle = typeof title === 'string' ? title.toLowerCase() : ''
  return normalizedTitle === 'values' || normalizedTitle === 'value'
}

export const getBulkAssignKpiPayload = (params, formState, objectivesGoalId, currentTimeZone) => {
  const isNewKpi = formState.kpi && typeof formState.kpi.value !== 'number'
  const effectiveFrom = getEffectiveFromDate(params.search_params.quarter, currentTimeZone)
  const effectiveTill = getEffectiveTillDate(params.search_params.quarter, params.search_params.review_period)

  return {
    reviewer_id: params.search_params.reviewer_id,
    approver_id: params.search_params.approver_id,
    performance_review_setting_id: params.search_params.review_setting,
    goal_id: objectivesGoalId,
    weightage: formState.weightage || 0,
    effective_from: effectiveFrom,
    effective_till: effectiveTill,
    assign_to_team: formState.assign_to === 'team',
    user_ids: formState.assign_to === 'users' ? formState.users.map((u) => u.value || u) : [],
    ...(isNewKpi
      ? {
          kpi_title: formState.kpi?.label || formState.kpi?.value,
          kpi_description: formState.kpi_description
        }
      : {
          kpi_id: formState.kpi?.value
        })
  }
}

export const canAccessMyTeamTab = (currentUser, permissions) => {
  if (!currentUser || !permissions) return false

  const permissionCheck = checkPermission(permissions, 'pms', 'general', 'my_team_tab', true)
  const hasMyTeamPermission = permissionCheck.isView() || permissionCheck.isFull()
  if (!hasMyTeamPermission) return false

  const subordinatesCount =
    currentUser.subordinates_as_manager_count ||
    currentUser.subordinates_as_lead_count ||
    currentUser.subordinates_as_reporting_to_count ||
    currentUser.subordinates_as_pse_count ||
    0

  const hasPmsTeamAssignment = currentUser.pms_assigned_as_reviewer || currentUser.pms_assigned_as_approver

  return subordinatesCount > 0 || hasPmsTeamAssignment
}

export const canAccessPmsPage = (currentUser, permissions) => {
  if (!permissions) return false

  const hasReviews = !checkPermission(permissions, 'pms', 'general', 'reviews', true).isNone()
  const hasMyTeam = canAccessMyTeamTab(currentUser, permissions)
  const hasConfiguration = !checkPermission(permissions, 'pms', 'general', 'configuration', true).isNone()
  const hasLibrary = !checkPermission(permissions, 'pms', 'general', 'library', true).isNone()

  return hasReviews || hasMyTeam || hasConfiguration || hasLibrary
}
